<script lang="ts" setup>
  import type { ShippingAddress } from '@storefront/resources';
  import { setModelWithContext } from '@othercommerce/foundation';

  interface Props {
    addresses: ShippingAddress[],
    filters: Object,
  }

  defineProps<Props>();
</script>
<template>
  <UiModal large open visitable>
    <FormController v-slot="{ data, processing, ctx, submit }" :action="$route('web.cart.shipping.address.fill')" method="PATCH">
      <SfHeading>
        <h2>{{ $t('SHOP.CART.HEADER.CHOOSE_FROM_SAVED_SHIPPING_ADDRESSES') }}</h2>
      </SfHeading>
      <Collapsible>
        <template #header>
          <SfHeading type="span">
            {{ $t('SHOP.HEADER.FILTER') }}
          </SfHeading>
        </template>
        <template #content>
          <FormController v-slot="{ submit, processing }" :action="$route('web.cart.shipping.address.choose')" :data="filters" class="address-filters" method="GET">
            <SfFormInput :label="$t('SHOP.CART.LABEL.NAME')" name="name" />
            <div class="address-filters__grid">
              <div class="address-filters__city">
                <SfFormInput :label="$t('SHOP.CART.LABEL.CITY')" name="city" />
              </div>
              <div class="address-filters__street">
                <SfFormInput :label="$t('SHOP.CART.LABEL.STREET')" name="street" />
              </div>
              <div class="address-filters__postcode">
                <SfFormInput :label="$t('SHOP.CART.LABEL.POSTCODE')" name="postcode" />
              </div>
            </div>
            <UiButtonGroup end>
              <SfButton :disabled="processing" :href="$route('web.cart.shipping.address.choose')">
                {{ $t('SHOP.ACTION.CLEAR') }}
              </SfButton>
              <SfButton :disabled="processing" primary @click="submit">
                {{ $t('SHOP.ACTION.FILTER') }}
              </SfButton>
            </UiButtonGroup>
          </FormController>
        </template>
      </Collapsible>
      <div v-if="Object.values(addresses).length > 0" class="addresses-grid">
        <SfTile v-for="address in addresses" :selected="data.id === address?.id" class="address-card" interactive @click="setModelWithContext('id', ctx, address?.id)" @keydown.space="setModelWithContext('id', ctx, address?.id)">
          <span v-if="address.isMedical" :title="$t('SHOP.CART.ARIA.MEDICAL_CONTRACTOR_IMPORTED_ADDRESS')" class="address-card__address-rpwdl">RPWDL</span>
          <address>
            <p v-if="address?.name" class="name" v-html="address?.name" />
            <p v-if="address?.company" class="company" v-html="address?.company" />
            <p v-if="address?.firstLine" class="first-line" v-html="address?.firstLine" />
            <p v-if="address?.secondLine" class="second-line" v-html="address?.secondLine" />
            <p v-if="address?.thirdLine" class="third-line" v-html="address?.thirdLine" />
            <p v-if="address?.region" class="region" v-html="address?.region" />
            <p v-if="address?.email" class="email" v-html="address?.email" />
            <p v-if="address?.phone" class="phone" v-html="address?.phone" />
          </address>
        </SfTile>
      </div>
      <div v-else>
        <SfDataPlaceholder>{{ $t('SHOP.CART.MESSAGE.NO_MATCHING_SHIPPING_ADDRESSES') }}</SfDataPlaceholder>
      </div>
      <UiButtonGroup end>
        <SfButton back>
          {{ $t('SHOP.ACTION.CANCEL') }}
        </SfButton>
        <SfButton :disabled="Object.values(data).length === 0" :loading="processing" primary type="submit" @click="submit">
          {{ $t('SHOP.ACTION.CHOOSE') }}
        </SfButton>
      </UiButtonGroup>
    </FormController>
  </UiModal>

  <RouterView />
</template>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .address-filters {
    display: flex;
    flex-direction: column;
    gap: var(--form-gap-y) var(--form-gap-x);

    &__grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: var(--form-gap-y) var(--form-gap-x);

      @include media.query(md) {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto auto;
      }
    }

    &__city {
      @include media.query(md) {
        grid-column: span 2;
      }
    }

    &__street {
      @include media.query(md) {
        grid-column: 1;
      }
    }

    &__postcode {
      @include media.query(md) {
        grid-column: 2;
      }
    }
  }

  .addresses-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }

  .address-card {
    address {
      font-style: normal;

      p {
        font-size: 0.875rem;
      }
    }

    &__address-rpwdl {
      font-size: 0.75rem;
      font-weight: var(--font-weight-bold);
      color: var(--primary-color);
    }

    .name, .company {
      font-weight: var(--font-weight-bold);
      font-size: 1rem;
    }
  }
</style>
